import React, { forwardRef, useEffect, useState } from 'react';
import { useMediaBlob } from '../hooks/useMediaBlobUrl';
import { DEFAULT_PAGE_HEIGHT, SLIDE_DESIGN_WIDTH } from '../types';
import type { SlideElement, SlideVisual, TextParagraph, TextRun } from '../types';

/**
 * Tekent een vrije slide (achtergrond + elementen). Eén renderer voor preview, miniaturen, live beamer en
 * editor, zodat wat je bewerkt exact is wat het publiek ziet. Alle maten komen uit ontwerp-eenheden
 * (slide = 1000 breed, `page_height` hoog) en schalen mee via container-query-eenheden (cqw): 1cqw = 10 eenheden.
 * De slide past binnen elk vak met behoud van de beeldverhouding (16:9, 4:3, ...), met randen als het vak anders is.
 */

const CQ = (units: number) => `${(units / (SLIDE_DESIGN_WIDTH / 100)).toFixed(4)}cqw`;
const PCT_X = (units: number) => `${((units / SLIDE_DESIGN_WIDTH) * 100).toFixed(4)}%`;

export function pageHeightOf(visual: SlideVisual | null | undefined): number {
  const height = visual?.page_height;
  return typeof height === 'number' && height > 0 ? height : DEFAULT_PAGE_HEIGHT;
}

function withAlpha(hex: string | null | undefined, alpha: number): string | undefined {
  if (!hex) return undefined;
  const full = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
  const value = parseInt(full.slice(1), 16);
  if (Number.isNaN(value)) return undefined;
  return alpha >= 1 ? full : `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
}

function runStyle(run: TextRun): React.CSSProperties {
  const decorations = [run.underline ? 'underline' : '', run.strike ? 'line-through' : ''].filter(Boolean).join(' ');
  return {
    fontWeight: run.bold ? 700 : 400,
    fontStyle: run.italic ? 'italic' : 'normal',
    textDecoration: decorations || 'none',
    color: run.color || undefined,
    fontSize: run.font_size ? CQ(run.font_size) : undefined,
    fontFamily: run.font_family ? `"${run.font_family}", Arial, Helvetica, sans-serif` : 'Arial, Helvetica, sans-serif',
  };
}

function Paragraph({ paragraph }: { paragraph: TextParagraph }) {
  const size = paragraph.runs.find((run) => run.font_size)?.font_size;
  return (
    <p
      style={{
        margin: 0, whiteSpace: 'pre-wrap', textAlign: paragraph.align || 'left',
        lineHeight: paragraph.line_spacing || 1.2, minHeight: size ? CQ(size * (paragraph.line_spacing || 1.2)) : undefined,
        paddingLeft: paragraph.bullet ? CQ(24) : undefined, textIndent: paragraph.bullet ? CQ(-24) : undefined,
      }}
    >
      {paragraph.bullet && <span style={{ ...runStyle(paragraph.runs[0] || { text: '' }), fontWeight: 400 }}>{'•  '}</span>}
      {paragraph.runs.map((run, index) => <span key={index} style={runStyle(run)}>{run.text}</span>)}
    </p>
  );
}

function ElementText({ element }: { element: SlideElement }) {
  const text = element.text;
  if (!text) return null;
  const justify = { top: 'flex-start', middle: 'center', bottom: 'flex-end' }[text.anchor || 'top'];
  return (
    <div
      style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: justify,
        padding: CQ(text.padding ?? 7), boxSizing: 'border-box', overflowWrap: 'anywhere',
      }}
    >
      {text.paragraphs.map((paragraph, index) => <Paragraph key={index} paragraph={paragraph} />)}
    </div>
  );
}

function ElementImage({ element, previewMode }: { element: SlideElement; previewMode: boolean }) {
  const { url, failed } = useMediaBlob(element.media_id);
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [url]);
  if (failed || broken || !element.media_id) {
    return previewMode ? (
      <span role="status" style={{ fontSize: CQ(14), color: '#64748b', position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        Afbeelding niet beschikbaar
      </span>
    ) : null;
  }
  if (!url) return null;
  const crop = element.crop || {};
  const horizontal = 1 - (crop.left || 0) - (crop.right || 0);
  const vertical = 1 - (crop.top || 0) - (crop.bottom || 0);
  return (
    <img
      src={url} alt="" draggable={false} onError={() => setBroken(true)}
      style={{
        position: 'absolute', display: 'block', objectFit: element.fit === 'fill' || !element.fit ? 'fill' : element.fit,
        width: `${100 / horizontal}%`, height: `${100 / vertical}%`,
        left: `${(-(crop.left || 0) / horizontal) * 100}%`, top: `${(-(crop.top || 0) / vertical) * 100}%`,
        userSelect: 'none', pointerEvents: 'none',
      }}
    />
  );
}

export function SlideElementView({ element, pageHeight, previewMode }: { element: SlideElement; pageHeight: number; previewMode: boolean }) {
  const opacity = element.opacity ?? 1;
  const isShape = element.type === 'shape';
  const strokeWidth = element.stroke_width || 0;
  const radius = element.shape === 'ellipse' ? '50%' : element.shape === 'round_rect' ? CQ(Math.min(element.width, element.height) * 0.1) : undefined;
  const mirror = element.flip_x && element.type !== 'text' ? ' scaleX(-1)' : '';
  const style: React.CSSProperties = {
    position: 'absolute', left: PCT_X(element.x), top: `${((element.y / pageHeight) * 100).toFixed(4)}%`,
    width: PCT_X(element.width), height: `${((element.height / pageHeight) * 100).toFixed(4)}%`,
    transform: `rotate(${element.rotation || 0}deg)${mirror}`, transformOrigin: 'center',
    boxSizing: 'border-box', opacity: isShape ? 1 : opacity,
    backgroundColor: isShape && element.shape !== 'line' ? withAlpha(element.fill, opacity) : undefined,
    border: isShape && element.shape !== 'line' && strokeWidth && element.stroke ? `${CQ(strokeWidth)} solid ${element.stroke}` : undefined,
    borderRadius: radius, overflow: element.type === 'image' ? 'hidden' : undefined,
  };
  return (
    <div style={style} data-element-id={element.id} data-element-type={element.type}>
      {element.type === 'image' && <ElementImage element={element} previewMode={previewMode} />}
      {element.shape === 'line' && (
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, overflow: 'visible' }}>
          <line
            x1="0" y1="0" x2="100" y2="100" stroke={element.stroke || '#000'} vectorEffect="non-scaling-stroke"
            strokeWidth={`${CQ(strokeWidth || 1)}`} opacity={opacity}
          />
        </svg>
      )}
      {element.text && <ElementText element={element} />}
    </div>
  );
}

function Background({ visual }: { visual: SlideVisual }) {
  const background = visual.background;
  const { url } = useMediaBlob(background?.media_id);
  return (
    <>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: background?.color || '#ffffff' }} data-testid="slide-background" />
      {url && (
        <img
          src={url} alt="" draggable={false}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: background?.fit === 'fill' ? 'fill' : background?.fit || 'cover', pointerEvents: 'none' }}
        />
      )}
    </>
  );
}

interface Props {
  visual: SlideVisual;
  previewMode?: boolean;
  className?: string;
  /** Extra laag bovenop de elementen (selectiekaders in de editor). */
  children?: React.ReactNode;
  onBackgroundPointerDown?: (event: React.PointerEvent) => void;
  onElementPointerDown?: (event: React.PointerEvent, element: SlideElement) => void;
  onElementDoubleClick?: (element: SlideElement) => void;
  /** Toetsenbord: Enter/spatie op een onderdeel (maakt de onderdelen focusbaar en bedienbaar). */
  onElementActivate?: (element: SlideElement) => void;
  /** Onderdeel dat tijdelijk onzichtbaar is (de editor tekent het dan zelf, bv. tijdens inline tekstbewerking). */
  hiddenElementId?: string | null;
}

function describeElement(element: SlideElement): string {
  if (element.type === 'image') return 'Afbeelding';
  const text = (element.text?.paragraphs || []).map((p) => p.runs.map((r) => r.text).join('')).join(' ').trim();
  if (text) return `${element.type === 'text' ? 'Tekst' : 'Vorm met tekst'}: ${text.slice(0, 60)}`;
  return element.shape === 'line' ? 'Lijn' : 'Vorm';
}

const SlideVisualStage = forwardRef<HTMLDivElement, Props>(function SlideVisualStage(
  { visual, previewMode = true, className = '', children, onBackgroundPointerDown, onElementPointerDown, onElementDoubleClick, onElementActivate, hiddenElementId }, ref,
) {
  const pageHeight = pageHeightOf(visual);
  const ratio = SLIDE_DESIGN_WIDTH / pageHeight;
  return (
    <div className={`flex h-full w-full items-center justify-center ${className}`} style={{ containerType: 'size' }}>
      <div
        ref={ref}
        data-testid="slide-stage" data-page-height={pageHeight}
        style={{
          position: 'relative', overflow: 'hidden', containerType: 'inline-size', aspectRatio: `${ratio}`,
          width: `min(100cqw, 100cqh * ${ratio})`, userSelect: 'none',
          // Slepen met een vinger mag de pagina niet laten scrollen (alleen in de editor).
          touchAction: onElementPointerDown ? 'none' : undefined,
        }}
        onPointerDown={onBackgroundPointerDown}
      >
        <Background visual={visual} />
        {(visual.elements || []).map((element) => (
          <div
            key={element.id}
            style={element.id === hiddenElementId ? { visibility: 'hidden' } : undefined}
            onPointerDown={onElementPointerDown ? (event) => onElementPointerDown(event, element) : undefined}
            onDoubleClick={onElementDoubleClick ? () => onElementDoubleClick(element) : undefined}
            {...(onElementActivate ? {
              role: 'button', tabIndex: 0, 'aria-label': describeElement(element),
              onKeyDown: (event: React.KeyboardEvent) => {
                if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  event.stopPropagation();
                  onElementActivate(element);
                }
              },
            } : {})}
          >
            <SlideElementView element={element} pageHeight={pageHeight} previewMode={previewMode} />
          </div>
        ))}
        {children}
      </div>
    </div>
  );
});

export default SlideVisualStage;
